// @workflow assistant.ui-tools, assistant.send-message
/**
 * Show one task on its task board (`ui.tasks.show_on_board`, "Show this on the taskboard"): its card highlighted and
 * scrolled into view, Edit task left closed so the chat stays in view. Opening a task (Edit task) is another action,
 * actions/openTaskOnBoard.js openTaskOnBoard (Khalid, 2026-10-21). An in-app request, never a URL flag (a `?taskId` link does nothing when the
 * board already shows that link, and a page would need a watcher per parameter):
 *   requestShowTask({ taskId, boardId })  asks for it (actions/openTaskOnBoard.js showTaskOnBoard, after the board route);
 *   onShowTaskRequest(fn)                 the board on screen (Task/views/Kanban.vue) is told at once; a board that
 *                                         mounts or loads later takes the pending request with takeShowTaskRequest;
 *   registerShownBoardTask({ id, name })  the board, once the card is drawn and on screen;
 *   shownBoardTask()                      what `ui.tasks.show_on_board` reads back (Assistant/services/taskBoardHost.js),
 *                                         and the message context's task when Edit task is closed (messageContext.js).
 * Same pattern as actions/openTask.js: no `$refs` from outside.
 *
 * No imports, so `npm run test:unit` (node --test) can load it directly.
 */

let current = null;
let pending = null;
const listeners = new Set();

/**
 * @param {{ taskId: string|number, boardId: string|number }} request the task and the board it is on
 * @returns {{ taskId: string, boardId: string }} the request now pending
 */
export function requestShowTask({ taskId, boardId }) {
  pending = { taskId: String(taskId), boardId: String(boardId) };
  const request = pending;
  listeners.forEach((listener) => listener(request));
  return request;
}

/**
 * The pending request for this board, taken (so it is shown once), or null.
 * @param {string|number|null} boardId the board on screen
 */
export function takeShowTaskRequest(boardId) {
  if (!pending || boardId == null || pending.boardId !== String(boardId)) return null;
  const request = pending;
  pending = null;
  return request;
}

/**
 * @param {(request: { taskId: string, boardId: string }) => void} listener told of every request
 * @returns {() => void} stops listening
 */
export function onShowTaskRequest(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * @param {{ id: string|number, name?: string|null }} task the card on screen, named by taskSubjectName (actions/openTask.js)
 * @returns {() => void} unregisters it, unless another card registered since
 */
export function registerShownBoardTask(task) {
  const entry = { id: String(task.id), name: task.name || null };
  current = entry;
  return () => {
    if (current === entry) current = null;
  };
}

/** @returns {{ id: string, name: string|null }|null} the card the board shows highlighted, or null */
export function shownBoardTask() {
  return current;
}
